import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Flex, Button, Text, Table, Thead, Tbody, Tr, Th, Td, Input, HStack, IconButton, useToast, Badge, ButtonGroup } from "@chakra-ui/react";
import { FaTrash, FaArrowLeft, FaSearch } from 'react-icons/fa';

const itemsPerPage = 100;

export default function MasterFavorit({ onBack }) {
  const toast = useToast();
  const stickyHeaderRef = useRef(null);
  const [stickyTop, setStickyTop] = useState(0);

  const [allDiary, setAllDiary] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const showToast = (title, status = 'info') => {
    toast({ title, status, duration: 3000, isClosable: true, position: 'top' });
  };

  // Hitung pagination
  const totalPages = Math.ceil(allDiary.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const currentDiary = allDiary.slice(startIdx, endIdx);

  // Ukur tinggi header+filter biar sticky gak ketutup
  useEffect(() => {
    const updateStickyTop = () => {
      if (stickyHeaderRef.current) {
        setStickyTop(stickyHeaderRef.current.offsetHeight);
      }
    };
    updateStickyTop();
    window.addEventListener('resize', updateStickyTop);
    return () => window.removeEventListener('resize', updateStickyTop);
  }, []);

  const loadDiary = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/get-food-diary`, {
        params: { search, limit: 10000 }
      });
      setAllDiary(res.data || []);
      setCurrentPage(1); // reset ke page 1 pas search
    } catch (err) {
      showToast('Gagal load food diary', 'error');
    }
  };

  useEffect(() => { loadDiary(); }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data ini dari diary?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE}/delete-food-diary?id=${id}`);
      showToast('Data dihapus', 'success');
      loadDiary();
    } catch (err) {
      showToast('Gagal hapus', 'error');
    }
  };

  const formatTanggal = (tgl) => {
    if (!tgl) return '-';
    const d = new Date(tgl);
    return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
  };

  return (
    <Box p={{ base: 2, md: 4 }}>

      {/* STICKY HEADER + FILTER */}
      <Box
        ref={stickyHeaderRef}
        position="sticky"
        top="0"
        bg="white"
        zIndex={3}
        pb={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        mb={2}
      >
        <Flex justify="space-between" mb={3} align="center">
          <Button leftIcon={<FaArrowLeft />} onClick={onBack} variant="ghost" size="sm">Kembali</Button>
          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">Food Diary</Text>
          <Box w="80px" />
        </Flex>

        <HStack spacing={2}>
          <Input
            placeholder="Search user email / nama makanan..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            maxW="300px"
            size="sm"
          />
          <Button size="sm" onClick={() => setSearch('')}>Reset</Button>
        </HStack>
      </Box>

      {/* Tabel Desktop */}
      <Box overflowX="auto" borderWidth="1px" borderRadius="lg" maxH="60vh" overflowY="auto">
        <Table size="sm">
          <Thead
            bg="gray.50"
            position="sticky"
            top={`${stickyTop}px`}
            zIndex={2}
            boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          >
            <Tr>
              <Th textAlign="center">ID</Th>
              <Th>User Email</Th>
              <Th textAlign="center">Tanggal</Th>
              <Th textAlign="center">Meal</Th>
              <Th>Makanan</Th>
              <Th textAlign="center">Gram</Th>
              <Th textAlign="center">Kalori</Th>
              <Th textAlign="center">P</Th>
              <Th textAlign="center">C</Th>
              <Th textAlign="center">F</Th>
              <Th textAlign="center">Aksi</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentDiary.map(d => (
              <Tr key={d.id}>
                <Td textAlign="center">{d.id}</Td>
                <Td fontSize="xs">{d.user_email}</Td>
                <Td textAlign="center">{formatTanggal(d.tanggal)}</Td>
                <Td textAlign="center"><Badge colorScheme="blue">{d.meal_type}</Badge></Td>
                <Td fontWeight="semibold">{d.food_name}</Td>
                <Td textAlign="center">{d.gram}g</Td>
                <Td textAlign="center">{d.kalori} kkal</Td>
                <Td textAlign="center">{d.protein}g</Td>
                <Td textAlign="center">{d.carbs}g</Td>
                <Td textAlign="center">{d.fat}g</Td>
                <Td textAlign="center">
                  <IconButton icon={<FaTrash />} size="xs" colorScheme="red" onClick={() => handleDelete(d.id)} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Sticky Bawah */}
      {totalPages > 0 && (
        <Box position="sticky" bottom="0" bg="white" borderTop="1px solid" borderColor="gray.200" p={3} zIndex={3} mt={2}>
          <Flex justify="space-between" align="center">
            <Text fontSize="sm">Page {currentPage} / {totalPages} | Data {startIdx + 1}-{Math.min(endIdx, allDiary.length)} dari {allDiary.length}</Text>
            <ButtonGroup size="sm">
              <Button onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>First</Button>
              <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1}>Prev</Button>
              <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>Next</Button>
              <Button onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>Last</Button>
            </ButtonGroup>
          </Flex>
        </Box>
      )}

      {allDiary.length === 0 && (
        <Text textAlign="center" mt={8} color="gray.500">Belum ada data food diary</Text>
      )}
    </Box>
  );
}